import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ALL_QUALITY_RECORD_KINDS, type MoneyByCurrency } from '@lims/shared-types';
import { dashboardApi } from '../../api/endpoints.js';
import { ErrorState, LoadingState, PageHeader, Panel } from '../../components/ui/index.js';
import { formatMoney, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { formatCalendarDate } from '../work/status.js';
import { CountRows, HorizontalBars } from '../work/work-ui.js';

/**
 * The management dashboard (Phase 12, D174), redrawn for D278.
 *
 * COUNTS AND SUMS OF RECORDED FACTS — no percentage, forecast or pass rate. A
 * section the server returns as `null` is one this person may not see, and it
 * is ABSENT here, never drawn as zero. The finance section in particular is
 * decided by the server from `finance.view` (p.7): this page cannot show a
 * figure the server did not send.
 *
 * Bars appear only where the figures are counts of one kind side by side
 * (open work per department, open quality records per kind); each prints its
 * number and has a table equivalent. Money is never drawn as a bar: amounts
 * are per currency and are never added or compared across currencies.
 */
export function ManagementDashboardPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const data = useQuery({ queryKey: ['dashboard', 'management'], queryFn: () => dashboardApi.management() });

  if (data.isLoading) return <LoadingState label={t('common.loading')} />;
  if (data.error) return <ErrorState error={data.error} onRetry={() => void data.refetch()} />;
  const view = data.data;
  if (!view) return null;

  const count = (value: number) => formatNumber(value, { locale }) ?? String(value);
  const money = (rows: readonly MoneyByCurrency[]) =>
    rows.length === 0
      ? t('dashboards.noAmounts')
      : rows.map((row) => formatMoney(row.amountMinor, { locale, currency: row.currency })).join(' · ');

  return (
    <>
      <PageHeader
        title={t('dashboards.title')}
        subtitle={t('dashboards.subtitle', { date: formatCalendarDate(view.asOf, locale) ?? view.asOf })}
        breadcrumbs={[{ label: t('nav.managementDashboard') }]}
      />

      <div className="grid grid-cols-1 gap-gutter lg:grid-cols-2">
        {view.workByDepartment ? (
          <Panel title={t('dashboards.workTitle')} description={t('dashboards.workHint')}>
            {view.workByDepartment.length === 0 ? (
              <p className="text-body text-default-700">{t('dashboards.noOpenWork')}</p>
            ) : (
              <HorizontalBars
                data={view.workByDepartment.map((row) => ({
                  key: row.departmentId ?? 'none',
                  label: row.departmentName || t('dashboards.noDepartment'),
                  value: row.open,
                  detail: t('dashboards.delayedCount', { value: count(row.delayed) }),
                  detailValue: count(row.delayed),
                  detailIsAttention: row.delayed > 0,
                }))}
                format={count}
                labelHeader={t('dashboards.departmentColumn')}
                valueHeader={t('dashboards.openColumn')}
                detailHeader={t('dashboards.delayedColumn')}
                caption={t('dashboards.workTitle')}
              />
            )}
          </Panel>
        ) : null}

        {view.quality ? (
          <Panel title={t('dashboards.qualityTitle')} description={t('dashboards.qualityHint')}>
            <div className="flex flex-col gap-stack">
              <HorizontalBars
                data={ALL_QUALITY_RECORD_KINDS.map((kind) => ({
                  key: kind,
                  label: t('dashboards.openOfKind', { kind: t(`qualityKind.${kind}`) }),
                  value: view.quality!.openByKind[kind],
                  to: `/quality?kind=${kind}`,
                }))}
                format={count}
                labelHeader={t('dashboards.kindColumn')}
                valueHeader={t('dashboards.openColumn')}
                caption={t('dashboards.qualityTitle')}
              />
              <CountRows
                items={[
                  {
                    label: t('dashboards.overdueRecords'),
                    value: count(view.quality.overdueRecords),
                    to: '/quality',
                    attention: 'warning',
                    isAboveZero: view.quality.overdueRecords > 0,
                  },
                  { label: t('dashboards.openActions'), value: count(view.quality.openActions), to: '/corrective-actions' },
                  {
                    label: t('dashboards.overdueActions'),
                    value: count(view.quality.overdueActions),
                    to: '/corrective-actions',
                    attention: 'warning',
                    isAboveZero: view.quality.overdueActions > 0,
                  },
                ]}
              />
            </div>
          </Panel>
        ) : null}

        {view.equipment ? (
          <Panel
            title={t('dashboards.equipmentTitle')}
            description={t('dashboards.equipmentHint', { days: count(view.equipment.noticeDays) })}
          >
            <CountRows
              items={[
                {
                  label: t('dashboards.outOfCalibration'),
                  value: count(view.equipment.outOfCalibration),
                  to: '/equipment',
                  attention: 'danger',
                  isAboveZero: view.equipment.outOfCalibration > 0,
                },
                {
                  label: t('dashboards.calibrationDueSoon'),
                  value: count(view.equipment.calibrationDueSoon),
                  to: '/equipment',
                  attention: 'warning',
                  isAboveZero: view.equipment.calibrationDueSoon > 0,
                },
                {
                  label: t('dashboards.maintenanceOverdue'),
                  value: count(view.equipment.maintenanceOverdue),
                  to: '/maintenance',
                  attention: 'warning',
                  isAboveZero: view.equipment.maintenanceOverdue > 0,
                },
                { label: t('dashboards.maintenanceDueSoon'), value: count(view.equipment.maintenanceDueSoon), to: '/maintenance' },
                {
                  label: t('dashboards.openFaults'),
                  value: count(view.equipment.openFaults),
                  to: '/maintenance/faults',
                  attention: 'warning',
                  isAboveZero: view.equipment.openFaults > 0,
                },
              ]}
            />
          </Panel>
        ) : null}

        {view.reports ? (
          <Panel title={t('dashboards.reportsTitle')} description={t('dashboards.reportsHint')}>
            <CountRows
              items={[
                {
                  label: t('dashboards.issuedNotDelivered'),
                  value: count(view.reports.issuedNotDelivered),
                  to: '/reports',
                },
              ]}
            />
          </Panel>
        ) : null}

        {view.finance ? (
          <Panel title={t('dashboards.financeTitle')} description={t('dashboards.financeHint')}>
            <CountRows
              items={[
                { label: t('dashboards.outstanding'), value: money(view.finance.outstanding), to: '/invoices', isText: true },
                { label: t('dashboards.overdue'), value: money(view.finance.overdue), to: '/invoices', isText: true },
                {
                  label: t('dashboards.overdueInvoiceCount'),
                  value: count(view.finance.overdueInvoiceCount),
                  to: '/invoices',
                  attention: 'warning',
                  isAboveZero: view.finance.overdueInvoiceCount > 0,
                },
                {
                  label: t('dashboards.receivedThisMonth'),
                  value: money(view.finance.receivedThisMonth),
                  to: '/payments',
                  isText: true,
                },
              ]}
            />
          </Panel>
        ) : null}
      </div>

      <p className="mt-gutter text-caption text-default-600">{t('dashboards.countsNotice')}</p>
    </>
  );
}
